import { makeAutoObservable, runInAction } from 'mobx';
import type {
  AcquisitionApi,
  AcquisitionBinding,
  AcquisitionBindings,
  AcquisitionChannel,
  AcquisitionFilter,
  AcquisitionSummary,
  AcquisitionUser,
} from '../services/acquisitionApi';

export class AcquisitionStore {
  channels: AcquisitionChannel[] = [];
  bindings: AcquisitionBinding[] = [];
  total = '—';
  snapshotAt = '';
  summaryNext = '';
  bindingsNext = '';
  selectedChannel = '';
  user: AcquisitionUser | null = null;
  queriedUserId = '';
  error = '';
  denied = false;
  summaryLoading = false;
  bindingsLoading = false;
  userLoading = false;
  filter: AcquisitionFilter = {};
  epoch = 0;
  detailEpoch = 0;
  userEpoch = 0;

  readonly api: AcquisitionApi;

  constructor(api: AcquisitionApi) {
    this.api = api;
    makeAutoObservable(this, { api: false }, { autoBind: true });
  }

  clear() {
    this.epoch++;
    this.detailEpoch++;
    this.userEpoch++;
    this.channels = [];
    this.bindings = [];
    this.user = null;
    this.queriedUserId = '';
    this.total = '—';
    this.snapshotAt = '';
    this.selectedChannel = '';
    this.summaryNext = '';
    this.bindingsNext = '';
    this.summaryLoading = false;
    this.bindingsLoading = false;
    this.userLoading = false;
  }

  fail(error: unknown, fallback: string) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (status === 401 || status === 403) {
      this.clear();
      this.denied = true;
      this.error = '登录状态或管理员资格已失效，请重新登录或联系管理员。';
    } else
      this.error = status === 429 ? '查询过于频繁，请稍后重试。' : fallback;
  }

  async loadSummary(filter?: AcquisitionFilter) {
    if (this.denied || (this.summaryLoading && filter === undefined)) return;
    const append = filter === undefined;
    if (!append) {
      this.clear();
      this.filter = { ...filter };
    }
    if (append && !this.summaryNext) return;
    const epoch = this.epoch;
    this.summaryLoading = true;
    this.error = '';
    try {
      const result: AcquisitionSummary = await this.api.summary(
        this.filter,
        append ? this.summaryNext : undefined,
      );
      runInAction(() => {
        if (epoch !== this.epoch) return;
        this.channels = append
          ? [...this.channels, ...result.items]
          : result.items;
        this.total = result.totalBoundUsers;
        this.snapshotAt = result.snapshotAt;
        this.summaryNext = result.nextPageToken;
      });
    } catch (error) {
      runInAction(() => {
        if (epoch === this.epoch)
          this.fail(error, '渠道汇总查询失败，请重试。');
      });
    } finally {
      runInAction(() => {
        if (epoch === this.epoch) this.summaryLoading = false;
      });
    }
  }

  async loadBindings(channel?: string) {
    if (this.denied) return;
    const append = channel === undefined;
    if (!append) {
      this.detailEpoch++;
      this.selectedChannel = channel;
      this.bindings = [];
      this.bindingsNext = '';
    }
    if (
      !this.selectedChannel ||
      (append && (!this.bindingsNext || this.bindingsLoading))
    )
      return;
    const epoch = this.epoch;
    const detailEpoch = this.detailEpoch;
    this.bindingsLoading = true;
    this.error = '';
    try {
      const result: AcquisitionBindings = await this.api.bindings(
        { ...this.filter, trafficContentId: this.selectedChannel },
        append ? this.bindingsNext : undefined,
      );
      runInAction(() => {
        if (epoch !== this.epoch || detailEpoch !== this.detailEpoch) return;
        this.bindings = append
          ? [...this.bindings, ...result.items]
          : result.items;
        this.bindingsNext = result.nextPageToken;
      });
    } catch (error) {
      runInAction(() => {
        if (epoch === this.epoch && detailEpoch === this.detailEpoch)
          this.fail(error, '绑定明细查询失败，请重试。');
      });
    } finally {
      runInAction(() => {
        if (epoch === this.epoch && detailEpoch === this.detailEpoch)
          this.bindingsLoading = false;
      });
    }
  }

  async lookupUser(id: string) {
    if (this.denied) return;
    const epoch = this.epoch;
    const userEpoch = ++this.userEpoch;
    this.user = null;
    this.queriedUserId = id;
    this.userLoading = true;
    this.error = '';
    try {
      const result = await this.api.user(id);
      runInAction(() => {
        if (epoch === this.epoch && userEpoch === this.userEpoch)
          this.user = result;
      });
    } catch (error) {
      runInAction(() => {
        if (epoch === this.epoch && userEpoch === this.userEpoch)
          this.fail(error, '用户来源查询失败，请重试。');
      });
    } finally {
      runInAction(() => {
        if (epoch === this.epoch && userEpoch === this.userEpoch)
          this.userLoading = false;
      });
    }
  }
}
